"use client";
import React, { useState, useEffect, useRef } from 'react';
import { Refrigerator, Thermometer, Sparkles, Settings, ArrowRight } from 'lucide-react';
import IphoneCarousel from './IphoneCarousel';
import EditableText from './EditableText';
import LeadModal from './ui/LeadModal';
import { useEditMode } from './EditModeProvider';
import ColorPicker from './ColorPicker';

const HeroSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const pauseTimeoutRef = useRef(null);
  const { getContent } = useEditMode();

  useEffect(() => {
    let timer;
    if (!isPaused) {
      timer = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % 3);
      }, 5000);
    }
    return () => clearInterval(timer);
  }, [isPaused]);

  useEffect(() => {
    // Очистка таймаута паузы при размонтировании компонента
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  const handleCardClick = (idx) => {
    if (idx < 3) {
      setActiveIndex(idx);
      setIsPaused(true);

      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }

      pauseTimeoutRef.current = setTimeout(() => {
        setActiveIndex(0);
        setIsPaused(false);
      }, 30000);
    }
  };

  const features = [
    {
      title: 'Холодильное оборудование',
      desc: 'Автоматический учет температуры внутри холодильных камер',
      icon: Refrigerator,
    },
    {
      title: 'Микроклимат',
      desc: 'Автоматический учет температуры и влажности в помещении',
      icon: Thermometer,
    },
    {
      title: 'Облучатели и рециркуляторы',
      desc: 'Автоматический учет времени работы рециркулятора',
      icon: Sparkles,
    },
    {
      title: 'Другое',
      desc: 'Уведомления при тревоге, задачи сотрудникам, онлайн мониторинг показателей, скачивание отчетов и пр.',
      icon: Settings,
    },
  ];

  const colorSettings = [
      { key: 'hero_bg_color_light', label: 'Фон секции (Светлая тема)', default: '#F8FAFC' },
      { key: 'hero_bg_color_dark', label: 'Фон секции (Темная тема)', default: '#111827' },
      { key: 'btn_quote_bg_color', label: 'Фон кнопки', default: '#17c6a6' },
      { key: 'btn_quote_text_color', label: 'Текст кнопки', default: '#FFFFFF' },
      { key: 'feature_active_bg', label: 'Активная карточка (Фон)', default: 'linear-gradient(101.46deg, #13547A -7.74%, #80D0C7 132.4%)' },
      { key: 'feature_active_text', label: 'Активная карточка (Текст)', default: '#FFFFFF' },
      { key: 'feature_inactive_bg_light', label: 'Неакт. карточка (Фон светлый)', default: '#FFFFFF' },
      { key: 'feature_inactive_bg_dark', label: 'Неакт. карточка (Фон темный)', default: '#111111' },
      { key: 'feature_inactive_text_light', label: 'Неакт. карточка (Текст светлый)', default: '#111827' },
      { key: 'feature_inactive_text_dark', label: 'Неакт. карточка (Текст темный)', default: '#FFFFFF' },
  ];

  const sectionStyle = {
      '--hero-bg-light': getContent('hero_bg_color_light', '#F8FAFC'),
      '--hero-bg-dark': getContent('hero_bg_color_dark', '#111827'),
      '--btn-bg': getContent('btn_quote_bg_color', '#17c6a6'),
      '--btn-text': getContent('btn_quote_text_color', '#FFFFFF'),
      '--feat-act-bg': getContent('feature_active_bg', 'linear-gradient(101.46deg, #13547A -7.74%, #80D0C7 132.4%)'),
      '--feat-act-text': getContent('feature_active_text', '#FFFFFF'),
      '--feat-inact-bg-light': getContent('feature_inactive_bg_light', '#FFFFFF'),
      '--feat-inact-bg-dark': getContent('feature_inactive_bg_dark', '#111111'),
      '--feat-inact-text-light': getContent('feature_inactive_text_light', '#111827'),
      '--feat-inact-text-dark': getContent('feature_inactive_text_dark', '#FFFFFF'),
  };

  return (
    <>
      <style>{`
        .hero-dynamic-bg {
          background: var(--hero-bg-light) !important;
        }
        .dark .hero-dynamic-bg {
          background: var(--hero-bg-dark) !important;
        }
        .feature-active-bg {
          background: var(--feat-act-bg) !important;
          border-color: var(--feat-act-bg) !important;
        }
        .feature-inactive-bg {
          background: var(--feat-inact-bg-light) !important;
        }
        .dark .feature-inactive-bg {
          background: var(--feat-inact-bg-dark) !important;
        }
        .btn-dynamic-bg {
          background: var(--btn-bg) !important;
          border-color: transparent !important;
        }
      `}</style>
      <section style={sectionStyle} className="w-full pt-10 pb-8 lg:pt-14 lg:pb-12 hero-dynamic-bg flex items-center overflow-hidden relative">
        <ColorPicker settings={colorSettings} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-12 lg:gap-20 xl:gap-24">
            
            {/* Left Column: Text & Features */}
            <div className="w-full lg:w-[60%] flex flex-col space-y-10">
              <div className="space-y-6">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 dark:text-white leading-[1.15]">
                  <span className="block">
                    <EditableText tagName="span" contentKey="hero_title_1" defaultValue="Контроллеры Санэкс для" />
                  </span>
                  <span className="block text-[#2563EB]">
                    <EditableText tagName="span" contentKey="hero_title_2" defaultValue="вашего бизнеса" />
                  </span>
                  <span className="block">
                    <EditableText tagName="span" contentKey="hero_title_3" defaultValue="под любые задачи" />
                  </span>
                </h1>
                <EditableText tagName="p" contentKey="hero_subtitle" defaultValue="Автоматическое заполнение журналов, непрерывный мониторинг параметров оборудования и помещений в одном приложении" className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl leading-relaxed" />
              </div>

              {/* Features Grid */}
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, idx) => {
                  const IconComponent = feature.icon;
                  const isLinked = idx < 3;
                  const isActive = isLinked && activeIndex === idx;

                  return (
                    <div 
                      key={idx} 
                      onClick={() => handleCardClick(idx)}
                      className={`p-6 rounded-2xl border shadow-sm flex flex-col items-start space-y-2 transition-all duration-300 ${isLinked ? 'cursor-pointer' : ''} ${
                        isActive 
                          ? 'feature-active-bg text-[var(--feat-act-text)] shadow-lg shadow-blue-500/20' 
                          : 'feature-inactive-bg border-gray-200 dark:border-gray-800 text-[var(--feat-inact-text-light)] dark:text-[var(--feat-inact-text-dark)]'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                          isActive ? 'bg-white/20' : 'bg-blue-50 dark:bg-blue-900/30'
                        }`}>
                          <IconComponent className={`w-5 h-5 transition-colors duration-300 ${
                            isActive ? 'text-white' : 'text-[#2563EB] dark:text-blue-400'
                          }`} />
                        </div>
                        <EditableText tagName="h3" contentKey={`feature_title_${idx}`} defaultValue={feature.title} className={`font-semibold text-lg transition-colors duration-300 ${
                          isActive ? 'text-[var(--feat-act-text)]' : 'text-gray-900 dark:text-white'
                        }`} />
                      </div>
                      <EditableText tagName="p" contentKey={`feature_desc_${idx}`} defaultValue={feature.desc} className={`text-sm pl-11 transition-colors duration-300 ${
                        isActive ? 'text-blue-100 opacity-90' : 'text-gray-500 dark:text-gray-400'
                      }`} />
                    </div>
                  );
                })}
              </div>

              {/* CTA Area (Desktop Only) */}
              <div className="hidden lg:flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 pt-2">
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold btn-dynamic-bg text-[var(--btn-text)] hover:opacity-90 rounded-xl transition-colors duration-200 shadow-lg shadow-[#17c6a6]/30 w-full sm:w-auto"
                >
                  <EditableText tagName="span" contentKey="hero_cta_btn" defaultValue="Получить расчёт стоимости" />
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                <EditableText tagName="span" contentKey="hero_cta_desc" defaultValue="Бесплатно, без обязательств" className="text-sm text-gray-500 dark:text-gray-400" />
              </div>
            </div>

            {/* Right Column: Phone Mockup */}
            <div className="w-full lg:w-[40%] flex justify-center lg:justify-end relative">
               {/* Background glow */}
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[600px] bg-blue-500/20 blur-[120px] rounded-full pointer-events-none"></div>
               <IphoneCarousel activeIndex={activeIndex} />
            </div>

          </div>

          {/* Mobile CTA Area (below iPhone) */}
          <div className="flex lg:hidden flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mt-10 w-full">
            <button 
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold btn-dynamic-bg text-[var(--btn-text)] hover:opacity-90 rounded-xl transition-colors duration-200 shadow-lg shadow-[#17c6a6]/30 w-full sm:w-auto"
            >
              <EditableText tagName="span" contentKey="hero_cta_btn" defaultValue="Получить расчёт стоимости" />
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            <EditableText tagName="span" contentKey="hero_cta_desc" defaultValue="Бесплатно, без обязательств" className="text-sm text-gray-500 dark:text-gray-400" />
          </div>

        </div>
      </section>

      <LeadModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} buttonColor={getContent('btn_quote_bg_color', '#17c6a6')} />
    </>
  );
};

export default HeroSection;